import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { FormValues } from "../form-schema";
import { AccountFileUpload } from "./account-file-upload";
import { CostCenterFileUpload } from "./cost-center-file-upload";
import { DebtorCreditorFileUpload } from "./debtor-creditor-file-upload";

interface DataImportProps {
  form: UseFormReturn<FormValues>;
}

export function DataImportStep({ form }: DataImportProps) {
  const { t } = useTranslation();

  const handleFileChange = (file: File | null, fieldName: keyof FormValues) => {
    if (file) {
      const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
      const allowedExtensions = [".csv", ".xlsx", ".xls"];
      if (!allowedExtensions.includes(fileExtension)) {
        toast.error(
          t("mandanten_create.step2.invalid_file_type", {
            formats: allowedExtensions.join(", "),
          }),
        );
        return;
      }

      // Just store the original file - we'll rename it only when submitting
      form.setValue(fieldName, file);
    } else {
      form.setValue(fieldName, null);
      // Clear debtors/creditors data when creditors file is removed
      if (fieldName === "debtorsCreditorsFile") {
        form.setValue("debtorsCreditors", undefined);
      }
      // Clear cost centers data when cost centers file is removed
      if (fieldName === "costCentersFile") {
        form.setValue("costCenters", undefined);
      }
      // Clear accounts data when accounts file is removed
      if (fieldName === "accountsFile") {
        form.setValue("accounts", undefined);
      }
    }
  };

  return (
    <div className="max-w-[600px] mx-auto">
      <h2 className="text-2xl font-semibold mb-6">
        {t("mandanten_create.step2.title")}
      </h2>

      {/* Kreditoren Section */}
      <DebtorCreditorSection form={form} handleFileChange={handleFileChange} />

      {/* Sachkonten Section */}
      <AccountSection form={form} handleFileChange={handleFileChange} />

      {/* Kostenstellen Section */}
      <CostCenterSection form={form} handleFileChange={handleFileChange} />
    </div>
  );
}

function DebtorCreditorSection({
  form,
  handleFileChange,
}: {
  form: UseFormReturn<FormValues>;
  handleFileChange: (file: File | null, fieldName: keyof FormValues) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4 mb-8">
      <h3 className="font-medium text-lg">
        {t("mandanten_create.step2.debtors_creditors")}
      </h3>
      <div className="border rounded-lg p-4 space-y-4">
        <p className="text-sm text-gray-600">
          {t("mandanten_create.step2.datev_steps")}
        </p>

        <img
          src="/datev_screenshot_debtors_creditors.png"
          alt={t(
            "mandanten_create.step2.datev_debtors_creditors_export_screenshot",
          )}
          width={900}
          height={600}
          className="rounded-lg border mt-4"
        />
        <p className="text-sm text-gray-600">
          {t("mandanten_create.step2.ensure_all_accounts")}
        </p>

        <p className="text-sm text-gray-600">
          <span className="mr-2">{t("mandanten_create.step2.more_info")}</span>
          <a
            href="https://tabula-accounting.notion.site/Gesch-ftspartner-aus-DATEV-exportieren-276eecb29d0480b4bfa7f8ea9fbc87c9"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {t("mandanten_create.step2.guide")}
          </a>
        </p>

        <FormField
          control={form.control}
          name="debtorsCreditorsFile"
          render={({ field }) => (
            <DebtorCreditorFileUpload
              label={t("mandanten_create.step2.debtors_creditors_csv_excel")}
              accept=".csv,.xlsx,.xls"
              onChange={(file) =>
                handleFileChange(file, "debtorsCreditorsFile")
              }
              onDebtorsCreditorsParsed={(debtorsCreditors) => {
                form.setValue("debtorsCreditors", debtorsCreditors);
              }}
              value={field.value}
            />
          )}
        />
        <FormField
          control={form.control}
          name="skipDebtorsCreditorsImport"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked: boolean) => {
                    field.onChange(checked);
                    // If checked, clear the file and creditors data
                    if (checked) {
                      form.setValue("debtorsCreditorsFile", null);
                      form.setValue("debtorsCreditors", undefined);
                    }
                  }}
                />
              </FormControl>
              <FormLabel className="text-sm font-medium text-foreground">
                {t("mandanten_create.step2.skip_debtors_creditors_import")}
              </FormLabel>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

function AccountSection({
  form,
  handleFileChange,
}: {
  form: UseFormReturn<FormValues>;
  handleFileChange: (file: File | null, fieldName: keyof FormValues) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4 mb-8">
      <h3 className="font-medium text-lg">
        {t("mandanten_create.step2.accounts")}
      </h3>
      <div className="border rounded-lg p-4 space-y-4">
        <p className="text-sm text-gray-600">
          {t("mandanten_create.step2.datev_steps")}
        </p>

        <img
          src="/datev_screenshot_accounts.png"
          alt={t("mandanten_create.step2.datev_export_screenshot")}
          width={900}
          height={600}
          className="rounded-lg border mt-4"
        />

        <p className="text-sm text-gray-600">
          {t("mandanten_create.step2.export_booked_accounts")}
          <br></br>
          <br></br>
          {t("mandanten_create.step2.export_previous_year")}
        </p>

        <p className="text-sm text-gray-600">
          <span className="mr-2">{t("mandanten_create.step2.more_info")}</span>
          <a
            href="https://tabula-accounting.notion.site/Onboarding-Wie-exportiert-man-den-DATEV-Kontenplan-nach-Tabular-0a93f949f14649839b3bfe533ce98e3e"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {t("mandanten_create.step2.guide")}
          </a>
        </p>

        <FormField
          control={form.control}
          name="accountsFile"
          render={({ field }) => (
            <AccountFileUpload
              label={t("mandanten_create.step2.accounts_csv_excel")}
              accept=".csv,.xlsx,.xls"
              onChange={(file) => handleFileChange(file, "accountsFile")}
              onAccountsParsed={(accounts) => {
                form.setValue("accounts", accounts);
              }}
              value={field.value}
            />
          )}
        />
        <FormField
          control={form.control}
          name="skipAccountsImport"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked: boolean) => {
                    field.onChange(checked);
                    if (checked) {
                      form.setValue("accountsFile", null);
                      form.setValue("accounts", undefined);
                    }
                  }}
                />
              </FormControl>
              <FormLabel className="text-sm font-medium text-foreground">
                {t("mandanten_create.step2.skip_accounts_import")}
              </FormLabel>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

function CostCenterSection({
  form,
  handleFileChange,
}: {
  form: UseFormReturn<FormValues>;
  handleFileChange: (file: File | null, fieldName: keyof FormValues) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <h3 className="font-medium text-lg">
        {t("mandanten_create.step2.cost_centers")}
      </h3>
      <div className="border rounded-lg p-4">
        <FormField
          control={form.control}
          name="hasCostCenters"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormDescription>
                {t("mandanten_create.step2.use_cost_centers")}
              </FormDescription>
              <FormControl>
                <RadioGroup
                  onValueChange={(value) => {
                    const boolValue = value === "true";
                    field.onChange(boolValue);
                    if (!boolValue) {
                      form.setValue("costCentersFile", null);
                      form.setValue("costCenters", undefined);
                    }
                  }}
                  value={field.value ? "true" : "false"}
                  className="flex flex-row space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="true" id="kostenstellen-true" />
                    <Label htmlFor="kostenstellen-true">
                      {t("mandanten_create.step2.yes")}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="false" id="kostenstellen-false" />
                    <Label htmlFor="kostenstellen-false">
                      {t("mandanten_create.step2.no")}
                    </Label>
                  </div>
                </RadioGroup>
              </FormControl>
              {field.value && (
                <div className="space-y-4 mt-4">
                  <FormField
                    control={form.control}
                    name="costCentersFile"
                    render={({ field: fileField }) => (
                      <CostCenterFileUpload
                        label={t(
                          "mandanten_create.step2.cost_centers_csv_excel",
                        )}
                        accept=".csv,.xlsx,.xls"
                        onChange={(file) =>
                          handleFileChange(file, "costCentersFile")
                        }
                        onCostCentersParsed={(costCenters) => {
                          form.setValue("costCenters", costCenters);
                        }}
                        value={fileField.value}
                      />
                    )}
                  />
                </div>
              )}
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
